// tslint:disable:max-file-line-count
import * as PropTypes from 'prop-types';
import * as React from 'react';

import { CheckboxField, Preloader, TextField, Tooltip } from '@cian/components';

import { Button } from '@cian/button-component';

import { Link } from '../../common/components/link';

import { IOfferSchema, IUserSchema, TCategory } from '@cian/api-models/offer-card/v1/get-offer-data';
import { TStatusFetch } from '../../store/contract_rent_sale';
import { getRandomId } from '../../utils/banners';
import { isEmail, isFakeEmail } from '../../utils/validate';
import { addAdFoxBanner } from '../adfox';
import * as Analytics from './tracking';
import * as styles from './index.css';
import { DEFAULT_DEPOSIT_PERCENTAGES, DEFAULT_TERM } from '../mortgage-calculator';

export interface IDataLayerEvent {
  eventAction: string;
}

export type TDealType = 'rent' | 'sale';

export interface IContractRentSaleProps {
  category: TCategory;
  dealType: TDealType;
  messageError: string;
  offer: IOfferSchema;
  statusFetch: TStatusFetch;
  user: IUserSchema | null;
  totalItems?: number;
  minRate?: number;
  mortgageWidgetMarkup?: string;
  fetching: boolean;
  amount: number;
}

export interface IContractRentSaleDispatch {
  contractRentSaleClearState(): void;
  contractRentSaleSetError(): void;
  submitSubscriptionContractRentSale(host: string, email: string, docType: string, subscribeNews: boolean): void;
}

export interface IContractRentSaleState {
  email: string;
  emailError: string;
  isSubscribeNews: boolean;
}

export type IProps = IContractRentSaleProps & IContractRentSaleDispatch;

const PR1_RANDOM_CONTRACT = getRandomId();

const calculateMonthlyPayment = (rate: number, amount: number, deposit: number, term: number) => {
  const termInMonth = term * 12;
  const rateMonths = (rate || 0) / 100 / 12;
  const annuityRate =
    (rateMonths * Math.pow(1 + rateMonths, termInMonth)) / (Math.pow(1 + rateMonths, termInMonth) - 1);

  return Math.round(annuityRate * (amount - deposit));
};

export class ContractRentSale extends React.Component<IProps, IContractRentSaleState> {
  private container: HTMLDivElement | null;

  public constructor(props: IProps) {
    super(props);

    this.state = {
      email:
        props.user && props.user.isAuthenticated && props.user.email && !isFakeEmail(props.user.email)
          ? props.user.email
          : '',
      emailError: '',
      isSubscribeNews: true,
    };
  }

  public static contextTypes = {
    makeRequest: PropTypes.func,
  };

  public static getDerivedStateFromProps(nextProps: IProps, prevState: IContractRentSaleState) {
    const { user: nextUser } = nextProps;

    if (nextUser && nextUser.isAuthenticated && nextUser.email && !isFakeEmail(nextUser.email) && !prevState.email) {
      return {
        email: nextUser.email,
        emailError: '',
      };
    }

    return null;
  }

  public componentDidMount() {
    window.addEventListener('scroll', this.handleShowContract);
  }

  public UNSAFE_componentWillUpdate(nextProps: IProps, nextState: IContractRentSaleState) {
    if (nextProps.statusFetch === 'ok') {
      Analytics.trackResponseSuccess(this.props.dealType);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.handleShowContract);
  }

  public render() {
    const { minRate, amount, user } = this.props;
    const deposit = Math.round((amount * DEFAULT_DEPOSIT_PERCENTAGES) / 100);
    const monthlyPayment = minRate && calculateMonthlyPayment(minRate, amount, deposit, DEFAULT_TERM);

    return (
      <div className={styles['container']}>
        <div className={styles['form-container']} ref={container => (this.container = container)}>
          <div className={styles['title']}>
            Образец договора {this.getTitle(this.props.dealType, this.props.category)}
          </div>
          {this.props.statusFetch === 'ok' ? (
            <div className={styles['success_submit']}>
              <div className={styles['message']}>
                На <strong>{this.state.email}</strong> отправлена ссылка на скачивание договора.
                <br />
                Удачных сделок.
              </div>
              <Link className={styles['resend']} onClick={this.handleResendSubmit}>
                Отправить ещё
              </Link>
            </div>
          ) : (
            <div>
              <div className={styles['sub_title']}>Укажите вашу электронную почту и мы вышлем образец договора</div>
              <form className={styles['form']} onSubmit={this.handleSubmit}>
                <div>
                  <Tooltip tooltipContent={this.state.emailError || this.props.messageError}>
                    <TextField
                      value={this.state.email}
                      onValueChange={this.handleEmailChange}
                      placeholder="Электронная почта"
                    />
                  </Tooltip>
                </div>
                <div>
                  <Button type="submit" theme="ghost" size="S" className={styles['submit_button']}>
                    {this.props.statusFetch === 'isLoad' ? (
                      <div className={styles['preloader']}>
                        <Preloader size={16} />
                      </div>
                    ) : (
                      'Отправить'
                    )}
                  </Button>
                </div>
                <div className={styles['subscription']}>
                  <CheckboxField
                    value={this.state.isSubscribeNews}
                    onValueChange={this.handleCheckboxChange}
                    label={<div>Хочу получать новости Cian.ru</div>}
                  />
                </div>
                {this.props.user && !this.props.user.isAuthenticated && (
                  <div className={styles['disclaimer']}>
                    При отправке вы принимаете условия&nbsp;
                    <a
                      href="https://www.cian.ru/help/about/rules/"
                      target="_blank"
                      rel="nofollow noopener"
                      className={styles['link']}
                    >
                      Пользовательского соглашения
                    </a>{' '}
                    и{' '}
                    <a
                      href="https://www.cian.ru/help/about/36526/"
                      target="_blank"
                      rel="nofollow noopener"
                      className={styles['link']}
                    >
                      Политики конфиденциальности
                    </a>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
        {addAdFoxBanner({
          type: 'contract',
          key: 'contract',
          makeRequest: this.context.makeRequest,
          offer: this.props.offer,
          randomId: PR1_RANDOM_CONTRACT,
          positionInfo: 'offer_card_contract-1',
          isAgent: !!user && !!user.isAgent,
          additionalPuids: {
            monthlyPayment: String(monthlyPayment),
          },
        })}
      </div>
    );
  }

  private handleShowContract = () => {
    if (
      // tslint:disable:no-any
      (window as any).dataLayer &&
      !(window as any).dataLayer.find(
        (event: IDataLayerEvent) =>
          // tslint:enable:no-any
          event.eventAction === 'contract_show',
      )
    ) {
      // tslint:disable-next-line:no-non-null-assertion
      const containerPositionMiddle = this.container!.offsetTop + this.container!.offsetHeight / 2;
      const windowScrollPosition = window.pageYOffset + window.innerHeight;

      if (windowScrollPosition > containerPositionMiddle) {
        window.removeEventListener('scroll', this.handleShowContract);

        Analytics.trackShowContract();
      }
    }
  };

  private getTitle = (dealType: TDealType, category: TCategory): string => {
    if (dealType === 'sale') {
      return 'купли-продажи';
    }

    if (dealType === 'rent' && category === 'dailyFlatRent') {
      return 'посуточной аренды';
    }

    if (dealType === 'rent') {
      return 'аренды';
    }

    return '';
  };

  private handleEmailChange = (email: string) => {
    this.setState({
      email,
      emailError: '',
    });

    this.props.contractRentSaleClearState();
  };

  private handleCheckboxChange = (isSubscribeNews: boolean) => {
    this.setState({
      isSubscribeNews,
    });
  };

  private handleResendSubmit = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    this.setState({
      email: '',
    });

    this.props.contractRentSaleClearState();
  };

  private handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (this.props.statusFetch === 'isLoad') {
      return;
    }

    const email = this.state.email.trim();

    if (!email) {
      this.setState({
        emailError: 'Укажите почту',
      });

      return;
    }

    if (!isEmail(email)) {
      this.setState({
        emailError: 'Email указан не верно',
      });

      return;
    }

    const docType = this.mapDealTypeToDocType(this.props.dealType, this.props.category);

    if (docType) {
      this.props.submitSubscriptionContractRentSale(window.location.host, email, docType, this.state.isSubscribeNews);
    } else {
      this.props.contractRentSaleSetError();
    }
  };

  private mapDealTypeToDocType = (dealType: TDealType, category: TCategory) => {
    if (dealType === 'sale') {
      return 'sale';
    }

    if (dealType === 'rent') {
      if (category === 'dailyFlatRent') {
        return 'rentdaily';
      }

      return 'rent';
    }

    return undefined;
  };
}
